import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Input, Button, Table, Popconfirm } from 'antd';
import Form from '../../components/Form';
import { showNotification, ERROR_TYPE } from '../../components/Notification';
import styles from './index.module.scss';
import { LAYOUT_0_24, VALIDATE_MESSAGES } from '../../constants/form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import {
  deleteChilren,
  getParentDetail,
  selectChildren,
  selectParent,
  updateParent,
  UserType,
} from '../../store/reducers/homeSlice';
import { RootState } from '../../store/reducers';
import moment from 'moment';
import AntdDatePicker from '../../components/Input/DatePicker';
import {
  deleteParent,
  getGiftApi,
  getLogEntriesApi,
  sendCustomMessageApi,
} from '../../api';
import WealthieBreadCrumb from '../../components/BreadCrumb';
import withLoading from '../../helpers/withLoadingHOC';
import WealthieTable from '../../components/WelthieTable';

const defaultStartDate = moment().subtract(1, 'months').format('DD/MM/YYYY');
const defaultEndDate = moment().format('DD/MM/YYYY');

type UserParam = {
  id: string;
};

const breadcrumbConfig = [
  {
    name: 'Wealthie',
  },
  {
    name: 'User',
    link: '/users',
  },
  {
    name: 'Detail',
  },
];

function UserDetail(): React.ReactElement {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<UserParam>();
  const parent = useSelector((state: RootState) => selectParent(state));
  const children = useSelector((state: RootState) => selectChildren(state));
  const [customMess, setCustomMess] = useState('');
  const [isDeleting, setDeleting] = useState<boolean>(false);

  const navigateToDetail = useCallback(
    (userId?: string) => () => {
      history.push(`/child/${id}/${userId}`);
    },
    [history, id],
  );

  const handleDeleteChildren = useCallback(
    (id?: string, parentId?: string) => {
      dispatch(deleteChilren(id, parentId));
    },
    [dispatch],
  );

  const collumns = useMemo(
    () => [
      {
        title: 'Id',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'First Name',
        dataIndex: 'first_name',
        key: 'first_name',
        render: function renderFirstName(text: string, row: UserType) {
          return <a onClick={navigateToDetail(row.id)}>{text}</a>;
        },
      },
      {
        title: 'Last Name',
        dataIndex: 'last_name',
        key: 'last_name',
        render: function renderLastName(text: string, row: UserType) {
          return <a onClick={navigateToDetail(row.id)}>{text}</a>;
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Updated Date',
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: function renderLastName(udpatedDate: string) {
          return <div>{moment(udpatedDate).format('DD/MM/YYYY HH:mm:ss')}</div>;
        },
      },
      {
        title: 'Action',
        dataIndex: 'operation',
        render: function renderDelete(_: string, record: UserType) {
          return (children || []).length >= 1 ? (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDeleteChildren(record.id, id)}
            >
              <a>Delete</a>
            </Popconfirm>
          ) : (
            <div />
          );
        },
      },
    ],
    [children, handleDeleteChildren, navigateToDetail, id],
  );

  const giftCollumns = useMemo(
    () => [
      {
        title: 'Amount',
        dataIndex: 'giftcard_amt',
        key: 'giftcard_amt',
      },
      {
        title: 'Sender Name',
        dataIndex: 'sender_name',
        key: 'sender_name',
      },
      {
        title: 'Internal Id',
        dataIndex: 'id',
        key: 'id',
        render: function renderGiftId(text: string, row: any) {
          return <Link to={`/gift-card/${row.id}`}>{text}</Link>;
        },
      },
    ],
    [],
  );

  const logCollumns = useMemo(
    () => [
      {
        title: 'Action Timestamp',
        dataIndex: 'action_timestamp',
        key: 'action_timestamp',
      },
      {
        title: 'Action Description',
        dataIndex: 'action_description',
        key: 'action_description',
      },
      {
        title: 'Action Notes',
        dataIndex: ' action_notes',
        key: 'action_notes',
      },
    ],
    [],
  );

  useEffect(() => {
    dispatch(getParentDetail(id));
  }, [dispatch, id]);

  // inital values form
  useEffect(() => {
    const initalValues = {
      firstName: parent?.first_name,
      lastName: parent?.last_name,
      dobr: parent?.date_of_birth
        ? moment(new Date(parent.date_of_birth), 'DD/MM/YYYY').utc(false)
        : moment(moment(), 'DD/MM/YYYY').utc(false),
      email: parent?.email,
      notes: parent?.notes,
    };
    form.setFieldsValue(initalValues);
  }, [parent, form]);

  const onFinish = useCallback(
    ({ firstName, lastName, dobr, notes }) => {
      dispatch(
        updateParent(
          { firstName, lastName, dob: dobr.format('DD/MM/YYYY'), notes },
          id,
        ),
      );
    },
    [dispatch, id],
  );

  const onFinishFailed = useCallback(() => {
    showNotification({
      type: ERROR_TYPE,
      message: 'Please fill in proper form values.',
    });
  }, []);

  const handleCustomMessChange = (event: any) => {
    setCustomMess(event.target.value);
  };

  const onSendCustomMessage = useCallback(async () => {
    try {
      await sendCustomMessageApi({ message: customMess, parentId: id });
      showNotification({
        type: 'success',
        message: 'Send message successfully!',
      });
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Can not send custom message, please try again',
      });
    }
  }, [id, customMess]);

  const customTitle = useMemo(
    () => (
      <div className={styles.custom_message_container}>
        Please enter your message and press OK
        <textarea
          onChange={handleCustomMessChange}
          placeholder="Your custom message"
        />
      </div>
    ),
    [],
  );

  const confirmDeleteUser = useCallback(async () => {
    try {
      setDeleting(true);
      await deleteParent(id);
      showNotification({
        type: 'success',
        message: 'Delete user successfully!',
      });
      setDeleting(false);
      history.push('/users');
    } catch (error) {
      setDeleting(false);
      showNotification({
        type: 'error',
        message: 'Somethings went wrong, please try again',
      });
    }
  }, [id, history]);

  return (
    <>
      <WealthieBreadCrumb breadcrumbConfig={breadcrumbConfig} />
      <div data-testid="edit_profile_page" className={styles.wrapper}>
        <div className={styles.edit_profile_wrapper}>
          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            validateMessages={VALIDATE_MESSAGES}
            {...LAYOUT_0_24}
            data-testid="edit_profile_form"
          >
            <Form.Item label="Email" name="email">
              <Input
                disabled
                aria-label="email-input"
                placeholder="Your Email"
              />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true }]}
            >
              <Input aria-label="fName-input" placeholder="Your First Name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true }]}
            >
              <Input aria-label="lName-input" placeholder="Your Last Name" />
            </Form.Item>
            <Form.Item label="Date" name="dobr" rules={[{ required: true }]}>
              <AntdDatePicker
                aria-label="dob-input"
                placeholder="Your Date Of Birth (DD/MM/YYYY)"
              />
            </Form.Item>
            <Form.Item label="Notes" name="notes" rules={[{ required: false }]}>
              <Input aria-label="notes-input" placeholder="Your notes" />
            </Form.Item>
            <Form.Item className={styles.user_action_wrapper}>
              <Popconfirm onConfirm={onSendCustomMessage} title={customTitle}>
                <Button type="primary">Send custom email</Button>
              </Popconfirm>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="save_button"
                className={styles.save_button}
              >
                Save
              </Button>
              {/* TODO: add condition to show or hide this button */}
              <Popconfirm
                onConfirm={confirmDeleteUser}
                title="Do you want delete this user?"
              >
                <Button loading={isDeleting} danger>
                  Delete
                </Button>
              </Popconfirm>
            </Form.Item>
          </Form>
          <Table
            title={() => 'Children'}
            rowKey="id"
            dataSource={children}
            columns={collumns}
          />
          <div>Giftcards</div>
          <WealthieTable
            extraApiParams={[id]}
            rowKey="id"
            api={getGiftApi}
            columns={giftCollumns}
            filterData="data.data"
            filterPagination="data.pagination.totalCount"
            onChangeCallBack={undefined}
          />
          <div>Log Entries</div>
          <WealthieTable
            rowKey="id"
            api={getLogEntriesApi}
            columns={logCollumns}
            extraApiParams={[defaultStartDate, defaultEndDate, id]}
            onChangeCallBack={undefined}
          />
        </div>
      </div>
    </>
  );
}

export default withLoading(UserDetail);
